import React from 'react';
import classNames from 'classnames';

// PR-SPIN.4: UILoader is used as the Suspense fallback for every lazy-loaded
// module across the app (PR-PERF.2 tab modules, mt5-notification,
// real-account-signup, verification-modal, routes.jsx). Earlier passes
// (PR-SPIN.2) swapped the legacy red circular SVG for a 3-dot spinner — but
// that left THREE different loaders on screen during boot (HTML cube → dots →
// cube). PR-SPIN.4 unifies every loading surface onto the single branded
// Forex Lab cube spinner (the 3 pulsing halos + cube `<picture>` mirrored from
// `index.html` and `templates/app/components/loading.jsx`), rendered at a small
// size (~56px) so it fits modals / lazy-tab boundaries without dropping a 120px
// cube into a small container.
//
// CSS is inlined so the spinner renders before the components SCSS bundle
// finishes loading. The cube image uses absolute `/public/...` paths so it
// resolves identically from any package. Props + default export are unchanged
// so the 8+ Suspense consumers keep working.
type TUILoader = {
    className?: string;
    classNameBlock?: string;
};

const FX_CUBE_CSS = `
.fx-ui-cube {
    position: relative;
    width: 56px;
    height: 56px;
    display: grid;
    place-items: center;
    margin: 0 auto;
}
.fx-ui-cube__halo {
    position: absolute;
    inset: 0;
    border-radius: 50%;
    border: 1px solid #7c3aed;
    opacity: 0;
    animation: fx-ui-halo-pulse 1.5s cubic-bezier(0.22, 0.61, 0.36, 1) infinite;
}
.fx-ui-cube__halo--2 {
    animation-delay: 0.5s;
    border-color: #3b82f6;
}
.fx-ui-cube__halo--3 {
    animation-delay: 1s;
    border-color: #d946ef;
}
.fx-ui-cube__wrap {
    width: 60%;
    height: 60%;
    position: relative;
    z-index: 1;
    animation: fx-ui-cube-bob 1.5s cubic-bezier(0.34, 1.56, 0.64, 1) infinite;
    transform-origin: 50% 60%;
}
.fx-ui-cube__img {
    width: 100%;
    height: 100%;
    object-fit: contain;
    animation: fx-ui-cube-squash 1.5s cubic-bezier(0.34, 1.56, 0.64, 1) infinite;
    transform-origin: 50% 100%;
    filter: drop-shadow(0 6px 14px rgba(91, 33, 182, 0.55));
}
@keyframes fx-ui-halo-pulse {
    0%   { transform: scale(0.55); opacity: 0.7; }
    100% { transform: scale(1.20); opacity: 0; }
}
@keyframes fx-ui-cube-bob {
    0%   { transform: translateY(-14%); }
    32%  { transform: translateY(6%); }
    55%  { transform: translateY(-4%); }
    75%  { transform: translateY(2%); }
    100% { transform: translateY(-14%); }
}
@keyframes fx-ui-cube-squash {
    0%, 30% { transform: scale(1, 1); }
    34%     { transform: scale(1.18, 0.82); }
    44%     { transform: scale(0.92, 1.10); }
    60%     { transform: scale(1.04, 0.96); }
    100%    { transform: scale(1, 1); }
}
@media (prefers-reduced-motion: reduce) {
    .fx-ui-cube__halo,
    .fx-ui-cube__wrap,
    .fx-ui-cube__img {
        animation: none !important;
    }
    .fx-ui-cube__halo { opacity: 0.3; }
}
`;

const UILoader = ({ className, classNameBlock }: TUILoader) => {
    const block_class = classNames(classNameBlock, 'block-ui');
    const loading_class = classNames('block-ui__loading', className);
    return (
        <div className={block_class}>
            <div className={loading_class}>
                <div className='block-ui__loading-spinner' role='status' aria-label='Loading'>
                    <style>{FX_CUBE_CSS}</style>
                    <span className='fx-ui-cube'>
                        <span className='fx-ui-cube__halo' />
                        <span className='fx-ui-cube__halo fx-ui-cube__halo--2' />
                        <span className='fx-ui-cube__halo fx-ui-cube__halo--3' />
                        <span className='fx-ui-cube__wrap'>
                            <picture>
                                <source srcSet='/public/images/forex-lab/spinner/cube-192.webp' type='image/webp' />
                                <img
                                    src='/public/images/forex-lab/spinner/cube-256.png'
                                    alt=''
                                    className='fx-ui-cube__img'
                                    width='34'
                                    height='34'
                                    decoding='async'
                                />
                            </picture>
                        </span>
                    </span>
                </div>
            </div>
        </div>
    );
};

export default UILoader;
